import * as React from "react";
import { IUser, IUsersAnalytics, TopicsChartFilter } from "../../../../App/Analytics/Common";
import {
  generateTopicsChartTicks,
  getTopicsChartData,
  getValueTotalsFromUsersInAnalytics,
} from "../../../../parsers/analytics";
import colors from "../../../../styles/colors";
import Span from "../../../utils/AnalyticsSpan";
import MoreButton, { More, CloseHelpTextButton } from "../common/MoreButton";
import TopicsChart, { ITotalLearnersData } from "./TopicsChart";
import {
  SectionContainer,
  SectionTitle,
  StatisticsContainer,
  ChartTextContainer,
  ChartDescriptionContainer,
  ChartContainer,
} from "../common/Charts.styles";
import { TopicsIcon } from "../AnalyticsIcons";
import { ITopicsChartData } from "../../../../App/Analytics/Common";
import { AnimatedUnmountWrapper } from "../../../utils/AnimationUnmountWrapper/AnimationUnmountWrapper";

interface ILessonsAnalyticsProps {
  filterFunction: TopicsChartFilter;
  useSelectedAsFilter: boolean;
  users: IUser[];
  usersAnalytics: IUsersAnalytics;
  selected: Set<string>;
  tag: string;
}

interface ILessonsAnalyticsState {
  showMore: boolean;
}

export default class LessonsAnalytics extends React.PureComponent<ILessonsAnalyticsProps, ILessonsAnalyticsState> {
  constructor(props: ILessonsAnalyticsProps) {
    super(props);
    this.state = { showMore: false };
  }

  public render() {
    const { filterFunction, users, usersAnalytics, selected } = this.props;
    const { averageValue: allAvg, maxValue: maxRange, minValue: allMin } = getValueTotalsFromUsersInAnalytics(
      users,
      usersAnalytics,
      "teachings"
    );
    const isFilterActive = selected.size > 0;
    const graphData = getTopicsChartData(allMin, maxRange, users, usersAnalytics, selected);
    const totalLearnersData = this.calculateTotalLearners(graphData);
    return (
      <SectionContainer>
        <StatisticsContainer>
          <ChartTextContainer>
            <SectionTitle>
              <TopicsIcon /> Lessons
            </SectionTitle>
            <ChartDescriptionContainer>
              <div>
                Learners took on average <Span color={colors.chartGreen100}>{Math.round(allAvg)}</Span> Lessons.
                <br />
                <br />
                The quickest Learners have taken as little as{" "}
                <Span color={colors.chartDarkBlue100}>{Math.round(allMin)}</Span> Lessons and the slowest as many as{" "}
                <Span color={colors.chartDarkBlue100}>{Math.round(maxRange)}</Span>.
              </div>
              <MoreButton onClick={this.onToggleShowMore} isOpenText={this.state.showMore} />
            </ChartDescriptionContainer>
          </ChartTextContainer>
          <ChartContainer>
            <TopicsChart
              data={graphData}
              filterFunction={filterFunction}
              ticks={generateTopicsChartTicks(graphData)}
              totalLearnersData={totalLearnersData}
              isFilterActive={isFilterActive}
            />
          </ChartContainer>
        </StatisticsContainer>
        <AnimatedUnmountWrapper isMounted={this.state.showMore} delayTime={300}>
          <More>
            <CloseHelpTextButton onClick={this.onToggleShowMore} isOpenText={this.state.showMore} />
            <p>
              The number of Lessons a Learner receives is the number of times they have to review the main content of a
              unit. This includes the initial Lesson before the first Post-Assessment attempt, as well as any additional
              reviews after failing a Post-Assessment.
            </p>
            <p>
              To complete the course with the shortest path, a Learner would have to take all possible Pre-Assessments
              and pass them on their first attempt.
            </p>
          </More>
        </AnimatedUnmountWrapper>
      </SectionContainer>
    );
  }

  public onToggleShowMore = () => {
    this.setState({ showMore: !this.state.showMore });
  };

  private calculateTotalLearners = (graphData: ITopicsChartData[]): ITotalLearnersData => {
    let totalLearners = 0;
    let totalFilteredLearners = 0;

    for (const topicData of graphData) {
      totalLearners += topicData.students ?? 0;
      totalFilteredLearners += topicData.filtered ?? 0;
    }

    return { totalLearners, totalFilteredLearners };
  };
}
